import React, { useEffect, useState } from "react"
import { useShallow } from "zustand/react/shallow"

// components
import LabelMenu from "../components/LabelMenu/LabelMenu"
import { Skeleton, Flex } from "antd"

// utils
import { registryValidMsgs } from "utilities/registryValidations"

// context
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"
import useAuthoringCommentsStore from "../../hooks/stores/useAuthoringCommentsStore"
import useAuthoringActions from "../../hooks/actions/useAuthoringActions"
import useAuthoringValidationsStore from "../../hooks/stores/useAuthoringValidationsStore"
import useAuthoringTranslations from "../../hooks/stores/useAuthoringTranslations"

// styles
import "./ObjectFieldTemplate.scss"

const ObjectFieldTemplate = (props) => {
  const [showMenu, setShowMenu] = useState(false)
  const [fieldComments, setFieldComments] = useState([])
  const { properties, uiSchema, idSchema } = props
  const [fieldLoading, setFieldLoading] = useState(false)

  const { registryValidations } = useAuthoringValidationsStore(
    useShallow((state) => ({
      registryValidations: state.registryValidations,
    }))
  )

  const { scrollToField } = useAuthoringActions()

  const { isTranslationActive, selectedLanguage } = useAuthoringTranslations(
    useShallow((state) => ({
      isTranslationActive: state.isTranslationActive,
      selectedLanguage: state.selectedLanguage,
    }))
  )

  const { openComment, setOpenComment, commentsMap } = useAuthoringCommentsStore(
    useShallow((state) => ({
      openComment: state.openComment,
      setOpenComment: state.setOpenComment,
      commentsMap: state.commentsMap,
    }))
  )

  const { formView, formSection, isReviewerView } = useAuthoringViewStore(
    useShallow((state) => ({
      formView: state.formView,
      formSection: state.formSection,
      isReviewerView: state.isReviewerView,
    }))
  )

  const options = uiSchema["ui:options"]

  let title = ""
  let schemaClassNames = {}
  const jpath = idSchema.$id.split(".").join("_")

  useEffect(() => {
    if (commentsMap) {
      if (commentsMap[jpath]) {
        let fieldComments = commentsMap[jpath]
        setFieldComments(fieldComments)
      } else {
        setFieldComments([])
      }
    }
  }, [commentsMap])

  useEffect(() => {
    if (openComment && jpath === openComment) {
      scrollToField(jpath)
    }
  }, [openComment])

  if (options) {
    schemaClassNames = options?.classNames
    title = options.title
    if (isTranslationActive && selectedLanguage && options?.translations?.[selectedLanguage]?.title) {
      title += ` (${options.translations[selectedLanguage].title})`
    }
  }

  let objectContainerClassNames = [
    `rjs-object-container${options?.hide || (options?.hideReviewer && isReviewerView) ? " hidden" : ""}`,
  ]
  if (schemaClassNames?.objectContainer) objectContainerClassNames.push(schemaClassNames.objectContainer)

  if (fieldLoading) return <Skeleton active />

  return (
    <div
      id={jpath + "-container"}
      className={objectContainerClassNames.join(" ")}
      onMouseEnter={() => setShowMenu(true)}
      onMouseLeave={() => setShowMenu(false)}
    >
      {title && (
        <div
          className="rjs-object-title-wrapper"
          id={jpath}
        >
          <Flex
            id={jpath + "-object-title-container"}
            className={`rjs-object-title-container ${
              schemaClassNames?.objectTitleContainer ? schemaClassNames.objectTitleContainer : ""
            }`}
            align="center"
            gap={7}
            // wrap
          >
            <div className={`rjs-object-title ${schemaClassNames?.objectTitle ? schemaClassNames.objectTitle : ""}`}>
              {title}
            </div>
            {title && (
              <LabelMenu
                hideFork={true}
                jpath={jpath}
                showMenu={showMenu}
                openComment={openComment}
                setShowMenu={setShowMenu}
                fieldComments={fieldComments}
                formView={formView}
                formSection={formSection}
                setFieldLoading={setFieldLoading}
                formContext={props.formContext}
                {...options}
                label={title}
                showLabel={false}
              />
            )}
          </Flex>
        </div>
      )}
      <div
        className={`rjs-object-inner-container ${
          schemaClassNames?.objectInnerContainer ? schemaClassNames.objectInnerContainer : ""
        }`}
      >
        {properties.map((prop, i) => {
          const { uiSchema } = prop.content.props
          const options = uiSchema["ui:options"]
          const jpath = prop.content.props.idSchema["$id"].split(".").join("_")

          let fieldClassNames = ["rjs-field-container"]

          if (options?.classNames?.fieldContainer) {
            fieldClassNames.push(options.classNames.fieldContainer)
          }

          if (isTranslationActive) {
            fieldClassNames.push("rjs-full")

            if (jpath === "root_protocol_type_of_research") {
              let el = fieldClassNames[1].replace("pr-8", "pr-24")
              fieldClassNames[1] = el
            } else if (jpath === "root_protocol_study_type") {
              let el = fieldClassNames[1].replace("pl-8", "pl-24")
              fieldClassNames[1] = el
            }
          }

          if (options?.hide || (options?.hideReviewer && isReviewerView)) return null

          return (
            <div
              className={fieldClassNames.join(" ")}
              key={prop.name + "-" + "i"}
              id={jpath + "-field-container"}
            >
              {prop.content}
            </div>
          )
        })}
      </div>
      {registryValidMsgs(registryValidations, jpath)}
    </div>
  )
}

export default ObjectFieldTemplate
